/**
 * Box Component
 *
 * A generic layout primitive that replaces raw div usage.
 * Useful for one-off consistent styling or when no other semantic component fits.
 *
 * @example
 * ```tsx
 * import { Box } from '@/ui';
 *
 * <Box className="relative w-full">
 *   Content
 * </Box>
 * ```
 */

import * as React from 'react';
import type { VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/utils/utils';
import { boxVariants } from './Box.variants';

interface BoxProps
  extends React.HTMLAttributes<HTMLDivElement>,
  VariantProps<typeof boxVariants> { }

export const Box = React.forwardRef<HTMLDivElement, BoxProps>(
  ({ className, padding, border, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(boxVariants({ padding, border, className }))}
        {...props}
      />
    );
  }
);

Box.displayName = 'Box';
